import React, { useState } from 'react';
import { Container, Card, Table, Badge, Button, Form, Spinner, Modal, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import {
  ClipboardDocumentListIcon,
  EyeIcon,
  PencilSquareIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TruckIcon,
  XCircleIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyOrders, statusColors } from './hooks/useCompanyOrders';
import { paymentsAPI } from '../../services/api';

const CompanyOrders: React.FC = () => {
  const {
    isExpired,
    orders,
    customers,
    loading,
    filters,
    setFilters,
    selectedOrder,
    setSelectedOrder,
    updatingStatus,
    deliveryEdit,
    setDeliveryEdit,
    editState,
    setEditState,
    editedTotal,
    selectedCustomer,
    customerStats,
    handleStatusChange,
    handleDeliveryDateSave,
    enterEditMode,
    cancelEdit,
    handleSaveEdit,
    formatDate,
    clearFilters,
    closeDetailModal,
    closeDetailModalSimple,
    cancelDeliveryEdit,
  } = useCompanyOrders();

  const [payLinkLoading, setPayLinkLoading] = useState<number | null>(null);
  const [payLinkToast, setPayLinkToast] = useState<{ url: string; orderNum: string } | null>(null);

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <ClipboardDocumentListIcon style={{ width: '24px', height: '24px', color: '#065f46' }} />
            <h5 className="fw-bold mb-0" style={{ color: '#065f46' }}>All Orders</h5>
            <Badge bg="success">{orders.length}</Badge>
          </div>
        </div>

        {/* Customer Summary */}
        {filters.customer && selectedCustomer && customerStats && !loading && (
          <div className="mb-4">
            {/* Customer name header */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #065f46, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 700, fontSize: '1rem',
              }}>
                {selectedCustomer.company_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h6 className="fw-bold mb-0" style={{ color: '#0f172a', fontSize: '1.05rem' }}>
                  {selectedCustomer.company_name}
                </h6>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{selectedCustomer.customer_id}</span>
              </div>
            </div>

            {/* Stats Grid */}
            <Row className="g-2 g-md-3 mb-3">
              {/* Total Revenue */}
              <Col xs={6} md={3}>
                <div className="mobile-stat-card" style={{
                  background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '16px', height: '100%',
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CurrencyDollarIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Revenue</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#065f46' }}>
                    ${customerStats.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                    from {customerStats.activeOrders} order{customerStats.activeOrders !== 1 ? 's' : ''}
                  </div>
                </div>
              </Col>

              {/* Total Orders */}
              <Col xs={6} md={3}>
                <div className="mobile-stat-card" style={{
                  background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '16px', height: '100%',
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ShoppingCartIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Total Orders</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                    {customerStats.totalOrders}
                  </div>
                  <div className="d-flex flex-wrap gap-1 gap-md-3" style={{ fontSize: '0.72rem', marginTop: '2px' }}>
                    <span style={{ color: '#059669' }}><TruckIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{customerStats.delivered} delivered</span>
                    {customerStats.cancelled > 0 && <span style={{ color: '#dc2626' }}><XCircleIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{customerStats.cancelled} cancelled</span>}
                    {customerStats.pending > 0 && <span style={{ color: '#d97706' }}><ClockIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{customerStats.pending} active</span>}
                  </div>
                </div>
              </Col>

              {/* Average Order */}
              <Col xs={6} md={3}>
                <div className="mobile-stat-card" style={{
                  background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '16px', height: '100%',
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ChartBarIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Avg Order</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                    ${customerStats.avgAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="d-flex flex-wrap gap-1 gap-md-3" style={{ fontSize: '0.72rem', marginTop: '2px' }}>
                    <span style={{ color: '#059669' }}><ArrowTrendingUpIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />${customerStats.largest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} max</span>
                    <span style={{ color: '#94a3b8' }}><ArrowTrendingDownIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />${customerStats.smallest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} min</span>
                  </div>
                </div>
              </Col>

              {/* Date Range */}
              <Col xs={6} md={3}>
                <div className="mobile-stat-card" style={{
                  background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '16px', height: '100%',
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CalendarDaysIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Order History</span>
                  </div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>
                    {formatDate(customerStats.firstOrder.toISOString())}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                    to {formatDate(customerStats.lastOrder.toISOString())}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
          <Card.Body className="py-2 px-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} sm={4} md={3}>
                <Form.Select size="sm" value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
              <Col xs={12} sm={4} md={3}>
                <Form.Select size="sm" value={filters.customer}
                  onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value }))}>
                  <option value="">All Customers</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto">
                {(filters.status || filters.customer) && (
                  <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Orders Table */}
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>
        ) : orders.length === 0 ? (
          <Card className="border-0 shadow-sm text-center py-5">
            <Card.Body>
              <ClipboardDocumentListIcon style={{ width: '48px', height: '48px', color: '#d1d5db' }} />
              <p className="text-muted mt-2">No orders found.</p>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead style={{ backgroundColor: '#f8fafc' }}>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th className="d-none d-md-table-cell">Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th className="d-none d-md-table-cell">Expected Delivery</th>
                      <th className="d-none d-md-table-cell">Delivered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <span className="fw-semibold">{order.order_number}</span>
                        </td>
                        <td>
                          <div>{order.customer_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{order.customer_id}</div>
                        </td>
                        <td className="d-none d-md-table-cell">{formatDate(order.order_date)}</td>
                        <td className="fw-semibold">
                          ${order.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={order.status}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            disabled={updatingStatus === order.id || isExpired}
                            style={{ width: '120px', fontSize: '0.78rem' }}
                            className={`text-${statusColors[order.status] || 'secondary'}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </Form.Select>
                        </td>
                        <td className="d-none d-md-table-cell">
                          {order.delivery_date ? formatDate(order.delivery_date) : '—'}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {deliveryEdit.orderId === order.id ? (
                            <div className="d-flex align-items-center gap-1">
                              <Form.Control
                                type="date"
                                size="sm"
                                value={deliveryEdit.tempDate}
                                onChange={(e) => setDeliveryEdit(prev => ({ ...prev, tempDate: e.target.value }))}
                                style={{ width: '140px', fontSize: '0.78rem' }}
                              />
                              <Button size="sm" variant="outline-success" className="p-1"
                                onClick={() => handleDeliveryDateSave(order.id)}>
                                ✓
                              </Button>
                              <Button size="sm" variant="outline-secondary" className="p-1"
                                onClick={cancelDeliveryEdit}>
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <span
                              role="button" tabIndex={0}
                              style={{ cursor: isExpired ? 'default' : 'pointer', fontSize: '0.82rem', color: order.delivered_at ? undefined : '#9ca3af' }}
                              onClick={() => {
                                if (!isExpired) {
                                  setDeliveryEdit({ orderId: order.id, tempDate: order.delivered_at || '' });
                                }
                              }}
                              onKeyDown={e => {
                                if ((e.key === 'Enter' || e.key === ' ') && !isExpired) {
                                  setDeliveryEdit({ orderId: order.id, tempDate: order.delivered_at || '' });
                                }
                              }}
                              title={isExpired ? '' : 'Click to edit'}
                            >
                              {order.delivered_at ? formatDate(order.delivered_at) : '—'}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-success" className="p-1"
                              title="View order details"
                              onClick={() => setSelectedOrder(order)}>
                              <EyeIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            {order.payment_status !== 'paid' && (
                              <Button size="sm" variant="outline-primary" className="p-1"
                                title="Generate payment link"
                                disabled={payLinkLoading === order.id}
                                onClick={async () => {
                                  setPayLinkLoading(order.id);
                                  try {
                                    const res = await paymentsAPI.createWholesaleLink(order.id);
                                    setPayLinkToast({ url: res.data.payment_link_url, orderNum: order.order_number });
                                  } catch (e: any) {
                                    alert(e?.response?.data?.error || 'Failed to generate payment link. Make sure your Stripe account is connected in Webstore Settings.');
                                  } finally {
                                    setPayLinkLoading(null);
                                  }
                                }}>
                                {payLinkLoading === order.id
                                  ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                                  : <LinkIcon style={{ width: '14px', height: '14px' }} />}
                              </Button>
                            )}
                            {order.payment_status === 'paid' && (
                              <span title="Paid" style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 700, padding: '2px 6px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>✓ Paid</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Order Detail Modal */}
        <Modal show={!!selectedOrder} onHide={closeDetailModal} size="lg">
          {selectedOrder && (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Order {selectedOrder.order_number}
                  {!editState.mode && (
                    <Button variant="link" size="sm" className="p-0 ms-2" title="Edit quantities" onClick={enterEditMode}>
                      <PencilSquareIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
                    </Button>
                  )}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row className="mb-3">
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Customer</div>
                    <div className="fw-semibold">{selectedOrder.customer_name}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Date</div>
                    <div className="fw-semibold">{formatDate(selectedOrder.order_date)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Status</div>
                    <Badge bg={statusColors[selectedOrder.status] || 'secondary'}>
                      {selectedOrder.status}
                    </Badge>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Expected Delivery</div>
                    <div className="fw-semibold">
                      {selectedOrder.delivery_date ? formatDate(selectedOrder.delivery_date) : '—'}
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Delivered</div>
                    <div className="fw-semibold">
                      {selectedOrder.delivered_at ? formatDate(selectedOrder.delivered_at) : '—'}
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="small text-muted">Total</div>
                    <div className="fw-bold" style={{ color: '#065f46' }}>
                      ${selectedOrder.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                    {selectedOrder.original_total_amount !== null && selectedOrder.original_total_amount !== selectedOrder.total_amount && (
                      <div className="small text-muted" style={{ textDecoration: 'line-through' }}>
                        Original: ${selectedOrder.original_total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                    {editState.mode && (
                      <div className="small" style={{ color: '#059669' }}>
                        New: ${editedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </Col>
                </Row>
                {selectedOrder.notes && (
                  <div className="mb-3">
                    <div className="small text-muted">Notes</div>
                    <div>{selectedOrder.notes}</div>
                  </div>
                )}
                <Table size="sm" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.product.name}</td>
                        <td className="text-muted">{item.product.sku}</td>
                        <td>
                          {editState.mode ? (
                            <Form.Control
                              type="number"
                              min={1}
                              size="sm"
                              style={{ width: 70 }}
                              value={editState.quantities[item.id] ?? item.quantity}
                              onChange={(e) => setEditState(prev => ({ ...prev, quantities: { ...prev.quantities, [item.id]: Math.max(1, parseInt(e.target.value) || 1) } }))}
                            />
                          ) : (
                            <>
                              {item.quantity}
                              {item.original_quantity !== null && item.original_quantity !== item.quantity && (
                                <span className="text-muted ms-1" style={{ textDecoration: 'line-through', fontSize: '0.8em' }}>
                                  {item.original_quantity}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td>${item.unit_price.toFixed(2)}</td>
                        <td className="fw-semibold">
                          {editState.mode
                            ? `$${((editState.quantities[item.id] ?? item.quantity) * item.unit_price).toFixed(2)}`
                            : `$${item.subtotal.toFixed(2)}`
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Modal.Body>
              <Modal.Footer>
                {editState.mode ? (
                  <>
                    <Button variant="outline-secondary" size="sm" onClick={cancelEdit} disabled={editState.saving}>Cancel</Button>
                    <Button variant="success" size="sm" onClick={handleSaveEdit} disabled={editState.saving}>
                      {editState.saving ? <><Spinner size="sm" className="me-1" /> Saving...</> : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline-secondary" size="sm" onClick={closeDetailModalSimple}>Close</Button>
                )}
              </Modal.Footer>
            </>
          )}
        </Modal>

        {/* Wholesale Payment Link Toast */}
        <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
          <Toast show={!!payLinkToast} onClose={() => setPayLinkToast(null)} delay={20000} autohide
            style={{ minWidth: 320, border: '1px solid #bbf7d0' }}>
            <Toast.Header style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
              <strong className="me-auto" style={{ color: '#065f46' }}>
                💳 Payment Link Created — Order {payLinkToast?.orderNum}
              </strong>
            </Toast.Header>
            <Toast.Body style={{ padding: '12px 16px' }}>
              <p style={{ fontSize: '0.82rem', color: '#374151', marginBottom: 10 }}>
                Share this link with your client to collect payment online:
              </p>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 10, wordBreak: 'break-all', fontSize: '0.78rem', color: '#6366f1', border: '1px solid #e2e8f0' }}>
                {payLinkToast?.url}
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="success" style={{ borderRadius: 8, fontSize: '0.8rem' }}
                  onClick={() => { navigator.clipboard.writeText(payLinkToast?.url || ''); }}>
                  Copy Link
                </Button>
                <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8, fontSize: '0.8rem' }}
                  onClick={() => window.open(payLinkToast?.url, '_blank')}>
                  Open
                </Button>
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>

      </Container>
    </CompanyLayout>
  );
};

export default CompanyOrders;
